import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import dayjs from 'dayjs';
import dicomParser from 'dicom-parser';
import { message } from 'antd';
import {
  FiCheck,
  FiFile,
  FiFilm,
  FiLoader,
  FiRefreshCw,
  FiX,
} from 'react-icons/fi';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';

import { Button } from '../../common/Button';
import { FileWorkspace } from '../../FileWorkspace';
import { DicomViewer, VideoViewer } from '../../Viewers/Viewers';
import { SWEEPS } from '../../NewCaseReverseUpload/constants';
import { getVideoMetadata } from '../../NewCaseReverseUpload/utils';
import { useUnifiedUpload } from '../../../../helpers/api/UnifiedUpload/useUnifiedUpload';
import type { UnifiedStudyGroup } from '../../../../helpers/api/UnifiedUpload/unified';
import type { ClinicalPrenatalMeta } from './ClinicalPrenatalForm';

type UploadTab = 'OB' | 'BLIND';
type UploadKind = 'dicom' | 'video';
type StudyCategory = 'T1' | 'T2' | 'T3';

type UploadVideoMeta = {
  width: number;
  height: number;
  duration: number;
};

type UploadDicomMeta = {
  patientName?: string;
  patientId?: string;
  studyDate?: string;
  formattedDate?: string | null;
  sopInstanceUid?: string;
};

type UploadItem = {
  id: string;
  uid: string;
  file: File;
  type: UploadKind;
  videoMeta?: UploadVideoMeta;
  dicomData?: UploadDicomMeta;
};

export interface PrenatalMediaUploadDraft {
  hasFiles: boolean;
  fileCount: number;
}

export interface PrenatalMediaUploadSubmitResult {
  submitted: boolean;
  hasPendingFiles: boolean;
}

export interface PrenatalMediaUploadHandle {
  hasFiles: () => boolean;
  submit: (
    studyInstanceUid?: string | null
  ) => Promise<PrenatalMediaUploadSubmitResult>;
  reset: () => void;
}

export interface PrenatalMediaUploadFormProps {
  studyInstanceUid?: string | null;
  patientId: string | number;
  pregnancyMeta?: ClinicalPrenatalMeta;
  onDraftChange?: (draft: PrenatalMediaUploadDraft) => void;
  showSubmitButton?: boolean;
}

function deriveStudyCategory(trimesterLabel?: string | null): StudyCategory {
  const normalized = String(trimesterLabel ?? '').trim().toUpperCase();
  if (normalized === 'T1' || normalized.includes('1')) return 'T1';
  if (normalized === 'T2' || normalized.includes('2')) return 'T2';
  if (normalized === 'T3' || normalized.includes('3')) return 'T3';
  return 'T1';
}

function buildStudiesFromUploads(
  obFiles: UploadItem[],
  blindAssignments: Record<number, UploadItem>,
  studyCategory: StudyCategory
): UnifiedStudyGroup[] {
  const studies: UnifiedStudyGroup[] = [];
  const blindEntries = Object.entries(blindAssignments).sort(
    ([left], [right]) => Number(left) - Number(right)
  );
  const blindFiles = blindEntries.map(([, file]) => file);

  if (obFiles.length > 0) {
    const type = obFiles[0].type;
    const rawObDate = obFiles.find((file) => file.dicomData?.studyDate)?.dicomData
      ?.studyDate;
    const formattedObDate = rawObDate
      ? dayjs(rawObDate, 'YYYYMMDD').format('YYYY-MM-DD')
      : null;

    studies.push({
      examType: 'OB',
      studyCategory,
      study_date: formattedObDate,
      sop_instance_uids: type === 'dicom' ? obFiles.map((file) => file.uid) : [],
      dicomFiles: type === 'dicom' ? obFiles.map((file) => file.file) : [],
      videoFiles:
        type === 'video'
          ? obFiles.map((file) => ({
              file: file.file,
              ...file.videoMeta,
            }))
          : [],
    });
  }

  if (blindFiles.length > 0) {
    const type = blindFiles[0].type;
    studies.push({
      examType: 'BLIND',
      studyCategory,
      sop_instance_uids: type === 'dicom' ? blindFiles.map((file) => file.uid) : [],
      dicomFiles: type === 'dicom' ? blindFiles.map((file) => file.file) : [],
      videoFiles:
        type === 'video'
          ? blindEntries.map(([slot, file]) => ({
              file: file.file,
              ...file.videoMeta,
              sweep_index: Number(slot),
            }))
          : [],
    });
  }

  return studies;
}

const PrenatalMediaUploadForm = forwardRef<
  PrenatalMediaUploadHandle,
  PrenatalMediaUploadFormProps
>(function PrenatalMediaUploadForm(
  {
    studyInstanceUid,
    patientId,
    pregnancyMeta,
    onDraftChange,
    showSubmitButton = true,
  },
  ref
) {
  const { t } = useTranslation(['Patient', 'NewCaseWizard']);
  const obInputRef = useRef<HTMLInputElement>(null);
  const blindInputRef = useRef<HTMLInputElement>(null);

  const [activeTab, setActiveTab] = useState<UploadTab>('OB');
  const [activeUploadSlot, setActiveUploadSlot] = useState<number | null>(null);
  const [obFiles, setObFiles] = useState<UploadItem[]>([]);
  const [blindAssignments, setBlindAssignments] = useState<Record<number, UploadItem>>({});
  const [previewItem, setPreviewItem] = useState<UploadItem | null>(null);

  const studyCategory = useMemo(
    () => deriveStudyCategory(pregnancyMeta?.trimesterLabel),
    [pregnancyMeta?.trimesterLabel]
  );
  const lockedPatientId = useMemo(() => String(patientId ?? ''), [patientId]);
  const fileCount = obFiles.length + Object.keys(blindAssignments).length;

  const obTypeLock = useMemo(
    () => (obFiles.length > 0 ? obFiles[0].type : null),
    [obFiles]
  );
  const blindTypeLock = useMemo(() => {
    const files = Object.values(blindAssignments);
    return files.length > 0 ? files[0].type : null;
  }, [blindAssignments]);

  const studies = useMemo(
    () => buildStudiesFromUploads(obFiles, blindAssignments, studyCategory),
    [blindAssignments, obFiles, studyCategory]
  );

  useEffect(() => {
    onDraftChange?.({
      hasFiles: studies.length > 0,
      fileCount,
    });
  }, [fileCount, onDraftChange, studies.length]);

  const { mutateAsync: uploadCase, isPending: isUploading } = useUnifiedUpload({
    onSuccess: (result) => {
      const successKeys = new Set(result.successKeys);
      setObFiles((prev) => prev.filter((item) => !successKeys.has(item.uid)));
      setBlindAssignments((prev) => {
        const next: Record<number, UploadItem> = {};
        Object.entries(prev).forEach(([key, item]) => {
          if (!successKeys.has(item.uid)) {
            next[Number(key)] = item;
          }
        });
        return next;
      });
    },
  });

  const handleBlindClick = (sweepId: number) => {
    setActiveUploadSlot(sweepId);
    blindInputRef.current?.click();
  };

  const handleRemoveOb = (id: string) =>
    setObFiles((prev) => prev.filter((file) => file.id !== id));

  const handleRemoveBlind = (sweepId: number) =>
    setBlindAssignments((prev) => {
      const next = { ...prev };
      delete next[sweepId];
      return next;
    });

  const handleBlindDrop = (sourceId: number, targetId: number) => {
    if (sourceId === targetId) return;
    setBlindAssignments((prev) => {
      const next = { ...prev };
      const sourceFile = next[sourceId];
      const targetFile = next[targetId];
      next[targetId] = sourceFile;
      if (targetFile) {
        next[sourceId] = targetFile;
      } else {
        delete next[sourceId];
      }
      return next;
    });
  };

  const processFile = async (file: File): Promise<UploadItem | null> => {
    const isDicom = file.name.toLowerCase().endsWith('.dcm');
    const isVideo = file.type.startsWith('video/');

    if (!isDicom && !isVideo) {
      return null;
    }

    let uid = `${file.name}-${file.size}`;
    const id = uid;

    let videoMeta: UploadVideoMeta | undefined;
    let dicomData: UploadDicomMeta | undefined;

    if (isDicom) {
      try {
        const headerBlob = file.slice(0, 5000000);
        const buffer = await headerBlob.arrayBuffer();
        let ds;
        try {
          ds = dicomParser.parseDicom(new Uint8Array(buffer));
        } catch (error: any) {
          if (error.dataSet) {
            ds = error.dataSet;
          } else {
            throw error;
          }
        }

        if (ds) {
          const rawName = ds.string('x00100010') || '';
          const pid = ds.string('x00100020') || 'unknown';
          const studyDateStr = ds.string('x00080020');
          const sopUid = ds.string('x00080018');

          if (sopUid) {
            uid = sopUid;
          }

          let formattedDate = null;
          if (studyDateStr && studyDateStr.length === 8) {
            const date = dayjs(studyDateStr, 'YYYYMMDD');
            if (date.isValid()) {
              formattedDate = date.format('DD/MM/YYYY');
            }
          }

          dicomData = {
            patientName: rawName.replace(/\^/g, ' ').trim(),
            patientId: pid,
            studyDate: studyDateStr || '',
            formattedDate,
            sopInstanceUid: sopUid,
          };
        }
      } catch (error) {
        console.warn('Failed to parse DICOM header:', error);
      }
    } else {
      videoMeta = await getVideoMetadata(file);
    }

    return {
      id,
      uid,
      file,
      type: isDicom ? 'dicom' : 'video',
      videoMeta,
      dicomData,
    };
  };

  const handleUpload = async (files: File[], targetTab: UploadTab) => {
    const newItems: UploadItem[] = [];
    const currentLock = targetTab === 'OB' ? obTypeLock : blindTypeLock;

    const existingUids = new Set([
      ...obFiles.map((file) => file.uid),
      ...Object.values(blindAssignments).map((file) => file.uid),
    ]);

    for (const file of files) {
      const item = await processFile(file);
      if (!item) continue;
      if (existingUids.has(item.uid)) continue;
      const effectiveLock =
        currentLock || (newItems.length > 0 ? newItems[0].type : item.type);
      if (item.type !== effectiveLock) continue;
      newItems.push(item);
    }

    if (newItems.length === 0) {
      return;
    }

    if (targetTab === 'OB') {
      setObFiles((prev) => [...prev, ...newItems]);
    } else {
      setBlindAssignments((prev) => {
        const next = { ...prev };
        let itemIndex = 0;
        const preferredSlot = activeUploadSlot ?? null;

        if (preferredSlot && !next[preferredSlot] && itemIndex < newItems.length) {
          next[preferredSlot] = newItems[itemIndex];
          itemIndex += 1;
        }

        for (let slot = 1; slot <= 7 && itemIndex < newItems.length; slot += 1) {
          if (!next[slot]) {
            next[slot] = newItems[itemIndex];
            itemIndex += 1;
          }
        }

        return next;
      });
    }

    setActiveUploadSlot(null);
    setActiveTab(targetTab);
  };

  const handleReset = () => {
    setObFiles([]);
    setBlindAssignments({});
    setPreviewItem(null);
    setActiveUploadSlot(null);
    setActiveTab('OB');
  };

  const uploadCurrentDraft = async (
    overrideStudyInstanceUid?: string | null
  ): Promise<PrenatalMediaUploadSubmitResult> => {
    if (studies.length === 0) {
      message.warning(t('prenatalExam.upload.noFiles', { ns: 'Patient' }));
      return { submitted: false, hasPendingFiles: false };
    }

    const resolvedStudyInstanceUid =
      overrideStudyInstanceUid ?? studyInstanceUid;
    if (!resolvedStudyInstanceUid) {
      message.error(
        t('prenatalExam.messages.createMediaFirst', { ns: 'Patient' })
      );
      return { submitted: false, hasPendingFiles: studies.length > 0 };
    }

    const result = await uploadCase({
      study_instance_uid: resolvedStudyInstanceUid,
      blind_prediction: true,
      studies,
    });
    return {
      submitted: true,
      hasPendingFiles: result.failed.length > 0,
    };
  };

  useImperativeHandle(
    ref,
    () => ({
      hasFiles: () => studies.length > 0,
      submit: uploadCurrentDraft,
      reset: handleReset,
    }),
    [studies, studyInstanceUid]
  );

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-6 py-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <p className="text-sm font-medium text-slate-600 dark:text-gray-300">
            {t('prenatalExam.upload.workspaceHint', { ns: 'Patient' })}
          </p>
          <div className="flex flex-wrap gap-3">
            <Button
              type="button"
              variant="ghost"
              onClick={handleReset}
              disabled={isUploading}
              className="gap-2"
            >
              <FiRefreshCw className="h-4 w-4" />
              {t('prenatalExam.upload.reset', { ns: 'Patient' })}
            </Button>
            {showSubmitButton ? (
              <Button
                type="button"
                onClick={() => {
                  void uploadCurrentDraft();
                }}
                disabled={isUploading}
                className="gap-2 bg-slate-900 text-white hover:bg-slate-800"
              >
                {isUploading ? (
                  <>
                    <FiLoader className="h-4 w-4 animate-spin" />
                    {t('prenatalExam.upload.uploading', { ns: 'Patient' })}
                  </>
                ) : (
                  <>
                    <FiCheck className="h-4 w-4" />
                    {t('prenatalExam.upload.submit', { ns: 'Patient' })}
                  </>
                )}
              </Button>
            ) : null}
          </div>
        </div>

        <div className="relative rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="h-[640px] p-0">
            <FileWorkspace
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              obFiles={obFiles}
              setObFiles={() => {}}
              blindAssignments={blindAssignments}
              setBlindAssignments={() => {}}
              onRemoveOb={handleRemoveOb}
              onRemoveBlind={handleRemoveBlind}
              onBlindDrop={handleBlindDrop}
              obInputRef={obInputRef}
              blindInputRef={blindInputRef}
              targetPatientId={lockedPatientId || null}
              setPreviewItem={setPreviewItem}
              SWEEPS={SWEEPS}
              onBlindClick={handleBlindClick}
            />
          </div>
        </div>

        <input
          type="file"
          multiple
          hidden
          ref={obInputRef}
          accept=".dcm,video/*"
          onChange={(event) =>
            handleUpload(Array.from(event.target.files || []), 'OB')
          }
        />
        <input
          type="file"
          multiple
          hidden
          ref={blindInputRef}
          accept=".dcm,video/*"
          onChange={(event) =>
            handleUpload(Array.from(event.target.files || []), 'BLIND')
          }
        />

        {previewItem ? (
          <div className="fixed inset-0 z-[10000] bg-black flex flex-col animate-in fade-in duration-300">
            <div className="z-50 h-24 px-8 flex items-center justify-between bg-gradient-to-b from-black/90 via-black/40 to-transparent pointer-events-none">
              <div className="flex items-center gap-4 pointer-events-auto">
                <div className="bg-white/10 p-2 rounded-lg backdrop-blur-md border border-white/10 shadow-2xl">
                  {previewItem.type === 'dicom' ? (
                    <FiFile size={20} className="text-blue-400" />
                  ) : (
                    <FiFilm size={20} className="text-purple-400" />
                  )}
                </div>
                <div className="drop-shadow-lg">
                  <p className="text-[10px] text-white/50 uppercase font-bold tracking-[0.2em] leading-tight mb-0.5">
                    Previewing File
                  </p>
                  <span className="font-bold text-white/90 text-sm truncate max-w-lg block">
                    {previewItem.file.name}
                  </span>
                </div>
              </div>

              <button
                onClick={() => setPreviewItem(null)}
                className="pointer-events-auto text-white/60 hover:text-white bg-white/10 hover:bg-white/20 p-2.5 rounded-full transition-all backdrop-blur-md border border-white/10 shadow-xl"
              >
                <FiX size={28} />
              </button>
            </div>

            <div className="flex-1 w-full h-full bg-black flex items-center justify-center overflow-hidden">
              {previewItem.type === 'video' ? (
                <div className="w-full h-full">
                  <VideoViewer file={previewItem.file} />
                </div>
              ) : (
                <div className="w-full h-full">
                  <DicomViewer file={previewItem.file} />
                </div>
              )}
            </div>
          </div>
        ) : null}

        <style>{`
          @keyframes sweep-1 {
            0% { left: 45%; top: 90%; transform: rotate(90deg); opacity: 1; }
            30% { left: 45%; top: 60%; transform: rotate(90deg); opacity: 1; }
            35% { left: 85%; top: 60%; transform: rotate(0deg); opacity: 0; }
            40% { left: 85%; top: 60%; transform: rotate(0deg); opacity: 1; }
            100% { left: 15%; top: 60%; transform: rotate(0deg); opacity: 1; }
          }
          .ultrasound-sweep-1 { animation: sweep-1 4s ease-in-out infinite alternate; }
          @keyframes sweep-v { 0% { top: 35%; opacity: 0.8; transform: rotate(90deg); } 100% { top: 85%; opacity: 1; transform: rotate(90deg); } }
          .ultrasound-sweep-2 { left: 20%; animation: sweep-v 2s ease-in-out infinite alternate; }
          .ultrasound-sweep-3 { left: 45%; animation: sweep-v 2s ease-in-out infinite alternate 0.3s; }
          .ultrasound-sweep-4 { left: 70%; animation: sweep-v 2s ease-in-out infinite alternate 0.6s; }
          @keyframes sweep-h { 0% { left: 15%; opacity: 0.8; } 100% { left: 85%; opacity: 1; } }
          .ultrasound-sweep-5 { top: 45%; animation: sweep-h 2.5s ease-in-out infinite alternate; }
          .ultrasound-sweep-6 { top: 70%; animation: sweep-h 2.5s ease-in-out infinite alternate 0.5s; }
          @keyframes sweep-c { 0% { left: 20%; top: 75%; } 50% { left: 50%; top: 85%; } 100% { left: 80%; top: 75%; } }
          .ultrasound-sweep-7 { animation: sweep-c 3s ease-in-out infinite alternate; }
        `}</style>
      </div>
    </DndProvider>
  );
});

export { PrenatalMediaUploadForm };
export default PrenatalMediaUploadForm;
